import {
	Badge,
	Box,
	Icons,
	Inline,
	joinAttributes,
	RelativeTime,
	Stack,
	Text,
	Time,
	Component,
	Button,
	Grid,
	Col,
} from "@sembark-travel/ui-base"
import { Link } from "@sembark-travel/ui-router"
import { localOrUtcTimestampToLocalDate } from "@sembark-travel/datetime-utils"
import { WhatsAppContactAvatar } from "./conversation/ContactAvatar"
import { WhatsAppFollowToggle } from "./conversation/FollowToggle"
import { TWhatsAppThread, TWhatsAppThreadLink } from "./store"
import { WhatsAppThreadTranscript } from "./conversation/MessageList"
import { generatePath } from "../router-utils"
import { PhoneNumber } from "../Contacts"
import { Dialog } from "@sembark-travel/ui-dialog"
import { AddNewTrip } from "../Trips/NewItem"
import { getEditablePhoneNumber } from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { ITrip } from "../Trips"
import { ITripPlanRequest } from "../TripPlanRequests"

function Section({
	title,
	children,
	actions,
}: {
	title: string
	children: React.ReactNode
	actions?: React.ReactNode
}) {
	return (
		<Stack gap="3" paddingX="4" paddingY="4" borderBottomWidth="1">
			<Inline justifyContent="between">
				<Text
					fontSize="xs"
					color="muted"
					fontWeight="semibold"
					textTransform="uppercase"
					letterSpacing="wider"
				>
					{title}
				</Text>
				{actions}
			</Inline>
			{children}
		</Stack>
	)
}

export function WhatsAppThreadContext({
	thread,
	onRefresh,
}: {
	thread: TWhatsAppThread
	onRefresh: () => Promise<unknown>
}) {
	// One generic `links` array carries every attached record, whatever its
	// type; each row picks its card from `link.type`. A lead that already
	// became a trip is dropped here rather than in the card, so it does not
	// leave an empty bordered row behind.
	const links = (thread.links || []).filter(
		(link) => !(link.type === "trip_plan_requests" && link.data?.trip_id)
	)
	const trips = links
		.filter((link) => link.type === "trips")
		.map((t) => t.data)
		.filter((d) => d !== undefined)
	const members = thread.members || []
	const xhr = useXHR()
	const { hasPermission } = useCheckPermissions()
	const canCreateTrip = hasPermission(PERMISSIONS.CREATE_QUERIES)

	return (
		<Stack>
			<Stack
				gap="2"
				paddingX="4"
				paddingY="6"
				alignItems="center"
				borderBottomWidth="1"
				bgColor="default"
			>
				<WhatsAppContactAvatar
					name={thread.contact?.name}
					number={thread.contact?.contact_number}
					size="16"
				/>
				<Stack gap="px" alignItems="center" textAlign="center">
					<Text fontWeight="semibold" fontSize="md">
						{thread.contact?.name ||
							thread.contact?.contact_number ||
							"Customer"}
					</Text>
					{thread.contact?.name && thread.contact.contact_number ? (
						<Text fontSize="sm" color="muted">
							{thread.contact.contact_number}
						</Text>
					) : null}
					{/* The name shown is the one the business saved; WhatsApp marks
					the customer's own profile name with a tilde beside it. */}
					{thread.contact?.address_book_name &&
					thread.contact.profile_name &&
					thread.contact.profile_name !== thread.contact.address_book_name ? (
						<Text fontSize="sm" color="muted">
							~{thread.contact.profile_name}
						</Text>
					) : null}
					{thread.contact?.username ? (
						<Text fontSize="sm" color="muted">
							@{thread.contact.username}
						</Text>
					) : null}
				</Stack>
				<Badge
					title={
						thread.phone_number?.wa_business_account?.name ||
						"WhatsApp Business Account"
					}
				>
					{thread.phone_number?.display_phone_number
						? `WABA · ${thread.phone_number.display_phone_number}`
						: "WABA"}
				</Badge>
			</Stack>
			<Component initialState={false}>
				{({ state, setState }) => (
					<Section
						title={"Linked Trips"}
						actions={
							links.length && canCreateTrip ? (
								<Button inline onClick={() => setState(true)}>
									<Text as="span" fontSize="sm">
										<Icons.Plus size="3" /> New
									</Text>
								</Button>
							) : null
						}
					>
						{links.length ? (
							<Stack borderWidth="1" rounded="md" bgColor="default">
								{links.map((link, index) => (
									<Box
										key={link.id}
										borderTopWidth={index > 0 ? "1" : "0"}
										padding="2"
									>
										<LinkedRecordCard link={link} />
									</Box>
								))}
							</Stack>
						) : canCreateTrip ? (
							<Box>
								<Button
									onClick={() => setState(true)}
									size="sm"
									level="primary"
								>
									Create New
								</Button>
							</Box>
						) : (
							<Text fontSize="sm" color="muted">
								Not linked to a trip.
							</Text>
						)}
						<Box>
							<Dialog
								open={state}
								onClose={() => setState(false)}
								title="Create new Trip"
								fitContainer
							>
								<Dialog.Body>
									<Grid gap="4">
										<Col md={12} lg={4}>
											<Stack gap="1">
												<Text
													textTransform="uppercase"
													fontWeight="semibold"
													letterSpacing="wider"
												>
													Thread Transcript
												</Text>
												<WhatsAppThreadTranscript
													thread={thread}
													maxHeight="calc(100vh - 100px)"
													borderWidth="1"
													rounded="md"
													bgColor="inset"
													padding="4"
												/>
											</Stack>
										</Col>
										<Col md={12} lg>
											<AddNewTrip
												onSuccess={async (trip) => {
													await xhr.post(`/wa-threads/${thread.id}/links`, {
														model_type: "trips",
														model_id: trip.id,
													})
													showSnackbar(
														`Trip #${trip.id} created and linked to this conversation.`
													)
													setState(false)
													onRefresh()
												}}
												onCancel={() => setState(false)}
												initialValues={{
													trip_source: trips.length
														? trips[trips.length - 1].trip_source
														: undefined,
													trip_source_contact: trips.length
														? trips[trips.length - 1].trip_source_contact
														: undefined,
													tourist:
														trips.length &&
														trips[trips.length - 1].trip_source?.is_agent
															? undefined
															: {
																	name: thread.contact?.name || "",
																	phone_numbers: thread.contact?.contact_number
																		? [
																				getEditablePhoneNumber(
																					"+" + thread.contact.contact_number
																				),
																			]
																		: [],
																},
												}}
											/>
										</Col>
									</Grid>
								</Dialog.Body>
							</Dialog>
						</Box>
					</Section>
				)}
			</Component>
			<Section
				title="Members"
				actions={<WhatsAppFollowToggle thread={thread} onRefresh={onRefresh} />}
			>
				{members.length ? (
					<Stack gap="2">
						{members.map((member) => (
							<Inline key={member.id} gap="2" alignItems="center">
								<WhatsAppContactAvatar name={member.name} size="8" />
								<Text fontSize="sm">{member.name || `User #${member.id}`}</Text>
							</Inline>
						))}
					</Stack>
				) : (
					<Text fontSize="sm" color="muted">
						No one is following this conversation yet.
					</Text>
				)}
			</Section>

			<Section title="Recent activity">
				<Stack gap="2">
					{thread.last_inbound_message_at ? (
						<Inline gap="2" justifyContent="between" alignItems="center">
							<Text fontSize="sm" color="muted">
								Last received
							</Text>
							<RelativeTime
								fontSize="sm"
								timestamp={thread.last_inbound_message_at}
							/>
						</Inline>
					) : null}
					{thread.last_outbound_message_at ? (
						<Inline gap="2" justifyContent="between" alignItems="center">
							<Text fontSize="sm" color="muted">
								Last sent
							</Text>
							<RelativeTime
								fontSize="sm"
								timestamp={thread.last_outbound_message_at}
							/>
						</Inline>
					) : null}
					{thread.messages_count ? (
						<Inline gap="2" justifyContent="between" alignItems="center">
							<Text fontSize="sm" color="muted">
								Messages
							</Text>
							<Text fontSize="sm" fontWeight="semibold">
								{thread.messages_count}
							</Text>
						</Inline>
					) : null}
				</Stack>
			</Section>
		</Stack>
	)
}

/**
 * Picks the card for a link's type. A new threadable needs one more arm here
 * and its own card — nothing else in this pane changes.
 */
function LinkedRecordCard({ link }: { link: TWhatsAppThreadLink }) {
	if (link.type === "trips" && link.data) {
		return <LinkedTripCard trip={link.data} />
	}
	if (link.type === "trip_plan_requests" && link.data) {
		return <LinkedTripPlanRequestCard tripPlanRequest={link.data} />
	}
	// An unknown type, or a payload without `data` (the lean list shape).
	return (
		<Text fontSize="sm" color="muted">
			{link.label || `#${link.id}`}
		</Text>
	)
}

function LinkedTripCard({ trip }: { trip: ITrip }) {
	const destinations = (trip.destinations || [])
		.map((d) => d.name)
		.filter(Boolean)
		.join(", ")

	return (
		<Stack gap="1">
			<Link
				to={generatePath("/trips/:tripId", { tripId: String(trip.id) })}
				color="accent"
				fontWeight="semibold"
				fontSize="sm"
				target="_blank"
			>
				<Inline gap="2" justifyContent="between">
					<Box>
						{joinAttributes(
							`Trip ${trip.id}`,
							destinations ? destinations : null,
							trip.contact?.name
						)}
					</Box>
					<Icons.ArrowTopRightOnSquare size="3" />
				</Inline>
			</Link>
			<Inline gap="2" alignItems="center" flexWrap="wrap">
				<LinkedTripStage trip={trip} />
				<Box fontSize="sm" color="muted">
					{joinAttributes(
						<Time
							value={localOrUtcTimestampToLocalDate(
								trip.start_date_local,
								trip.start_date
							)}
							dateFormat="DD MMM"
						/>,
						trip.nights + "N",
						trip.pax_string,
						trip.contact?.phone_numbers ? (
							<PhoneNumber value={trip.contact.phone_numbers} iconOnly />
						) : null
					)}
				</Box>
			</Inline>
		</Stack>
	)
}

/**
 * Where the trip stands: the server's `current_status`, which folds the
 * outcome (converted, on hold, dropped) and the calendar (on trip, past)
 * into one label. Coloured the way the trips list groups them.
 */
function LinkedTripStage({ trip }: { trip: ITrip }) {
	const status = trip.current_status
	if (!status) {
		return null
	}
	switch (status) {
		case "Converted":
		case "On Trip":
			return <Badge success>{status}</Badge>
		case "On Hold":
			return <Badge warning>{status}</Badge>
		case "Canceled":
		case "Dropped":
			return <Badge danger>{status}</Badge>
		case "Past Trip":
			return <Badge>{status}</Badge>
		default:
			return <Badge accent>{status}</Badge>
	}
}

function LinkedTripPlanRequestCard({
	tripPlanRequest,
}: {
	tripPlanRequest: ITripPlanRequest
}) {
	const pax = [
		tripPlanRequest.no_of_adults ? `${tripPlanRequest.no_of_adults}A` : null,
		tripPlanRequest.no_of_children
			? `${tripPlanRequest.no_of_children}C`
			: null,
	]
		.filter(Boolean)
		.join(", ")

	return (
		<Stack gap="1">
			{/* The lead has no detail page of its own, so this opens the listing
			    filtered to it. `source=all` so archived leads still resolve. */}
			<Link
				to={generatePath("/trip-plan-requests")}
				query={{ q: `#${tripPlanRequest.id}`, source: "all" }}
				color="accent"
				fontWeight="semibold"
				fontSize="sm"
				target="_blank"
			>
				<Inline gap="2" justifyContent="between">
					<Box>
						{joinAttributes(
							`TPR ${tripPlanRequest.id}`,
							tripPlanRequest.destination || null,
							tripPlanRequest.name
						)}
					</Box>
					<Icons.ArrowTopRightOnSquare size="3" />
				</Inline>
			</Link>
			<Box fontSize="sm" color="muted">
				{joinAttributes(
					tripPlanRequest.start_date ? (
						<Time
							value={localOrUtcTimestampToLocalDate(
								undefined,
								tripPlanRequest.start_date
							)}
							dateFormat="DD MMM"
						/>
					) : null,
					tripPlanRequest.no_of_days ? `${tripPlanRequest.no_of_days}D` : null,
					pax || null,
					tripPlanRequest.phone_number ? (
						<PhoneNumber value={tripPlanRequest.phone_number} iconOnly />
					) : null
				)}
			</Box>
		</Stack>
	)
}
